import { useState, useEffect } from 'react';
import { apiJson } from './api';
import GuidanceSheet from './GuidanceSheet';

/**
 * Reusable species suggestion results panel.
 *
 * Props (spec):
 *   suggestions    array of normalized suggestion objects
 *   refImages      array of reference image URLs shown as a top strip (may be empty)
 *   quotaInfo      object from GET /id-quota (may be null)
 *   provider       'plantnet' | 'plantid'
 *   onAccept       fn(suggestion) — called when user taps a suggestion row
 *   onDismiss      fn() — called when user closes/dismisses
 *   onWishlist     fn(suggestion) — accepted but unused in Phase 1
 *   onCompare      fn(suggestion) — accepted but unused in Phase 1
 *
 * Implementation extras (not in spec, needed for identical AddPlant behavior):
 *   remainingRequests   number | null — PlantNet remaining calls from the ID response
 *   dismissLabel        string — label for the dismiss button when suggestions exist (default "None of these")
 *   emptyDismissLabel   string — label for the dismiss button when no suggestions (defaults to dismissLabel)
 *   onRetry             fn() — shows a second "Try Again" button when provided
 *   plantId             number|string — enables confirm-id selected_profile_id actions
 *   onConfirm           fn(updatedPlant, suggestion) — called after selected_profile_id succeeds
 */

// ---------------------------------------------------------------------------
// HowToTellThemApart — secondary section rendered below the suggestion list
// ---------------------------------------------------------------------------

function HowToTellThemApart({ suggestions, plantId, onConfirm, onAccept }) {
  const [assistData, setAssistData] = useState(null);
  const [glossarySheet, setGlossarySheet] = useState(null); // { label, plain_language, botanical_note, svg_key }
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectingProfileId, setSelectingProfileId] = useState(null);
  const [selectError, setSelectError] = useState(null);

  const suggestionsWithProfile = suggestions.filter(s => s.profile_id != null);
  const profileIds = suggestionsWithProfile.slice(0, 6).map(s => s.profile_id);

  useEffect(() => {
    if (profileIds.length < 2) return;
    let active = true;
    apiJson(`/id-assist?profiles=${profileIds.join(',')}`)
      .then(data => { if (active && data) setAssistData(data); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (profileIds.length < 2 || !assistData || !assistData.discriminating?.length) return null;

  // Build profile_id → display name map from the suggestions list
  const profileNames = {};
  for (const s of suggestionsWithProfile) {
    profileNames[s.profile_id] = s.species;
  }

  // discriminating is already sorted with unique_to_one first by the backend
  const uniqueToOneSet = new Set((assistData.unique_to_one || []).map(x => x.field));

  // Find highest-confidence pairwise note that has a guidance_note
  const confidenceOrder = { confirmed: 0, likely: 1, unverified: 2 };
  const bestNote = [...(assistData.pairwise_notes || [])]
    .filter(n => n.guidance_note)
    .sort((a, b) => (confidenceOrder[a.confidence] ?? 3) - (confidenceOrder[b.confidence] ?? 3))[0];

  async function handleTraitTap(fieldPath, fieldLabel) {
    try {
      const data = await apiJson(`/id-traits/glossary/${encodeURIComponent(fieldPath)}`);
      if (!data.plain_language) return;
      setGlossarySheet({ label: fieldLabel, ...data });
      setSheetOpen(true);
    } catch (_) {}
  }

  async function handleSelectProfile(suggestion) {
    if (!plantId || suggestion.profile_id == null) return;
    setSelectingProfileId(suggestion.profile_id);
    setSelectError(null);
    try {
      const updated = await apiJson(`/plants/${plantId}/confirm-id`, {
        method: 'POST',
        body: JSON.stringify({ selected_profile_id: suggestion.profile_id }),
      });
      if (onConfirm) onConfirm(updated, suggestion);
      else if (onAccept) onAccept(suggestion);
    } catch (err) {
      setSelectError(err.message);
    } finally {
      setSelectingProfileId(null);
    }
  }

  return (
    <div className="id-tell-apart">
      <div className="id-tell-apart-heading">How to tell these apart</div>

      {bestNote && (
        <div className="id-tell-apart-callout">
          {bestNote.guidance_note}
        </div>
      )}

      {plantId && (
        <div className="id-tell-apart-select-actions">
          {suggestionsWithProfile.slice(0, 6).map(s => (
            <button
              key={s.profile_id}
              type="button"
              className="btn btn-secondary btn-sm id-select-profile-btn"
              onClick={() => handleSelectProfile(s)}
              disabled={selectingProfileId != null}
            >
              {selectingProfileId === s.profile_id ? 'Selecting...' : `Select ${s.species}`}
            </button>
          ))}
        </div>
      )}
      {selectError && <p className="id-select-error">{selectError}</p>}

      <div className="id-tell-apart-traits">
        {assistData.discriminating.map(entry => (
          <div key={entry.field} className="id-tell-apart-trait">
            <button
              type="button"
              className="id-tell-apart-label"
              onClick={() => handleTraitTap(entry.field, entry.label)}
            >
              {entry.label}
              {uniqueToOneSet.has(entry.field) && (
                <span className="id-tell-apart-badge">unique</span>
              )}
            </button>
            <div className="id-tell-apart-values">
              {assistData.profiles
                .filter(p => profileNames[p.id] != null)
                .map(p => {
                  const raw = (entry.values_by_profile || {})[String(p.id)];
                  let display;
                  if (raw === undefined || raw === null) {
                    display = '—';
                  } else if (Array.isArray(raw)) {
                    display = raw.length ? raw.map(v => String(v).replace(/_/g, ' ')).join(', ') : '—';
                  } else if (raw === true) {
                    display = 'Present';
                  } else if (raw === false) {
                    display = 'Absent';
                  } else {
                    display = String(raw).replace(/_/g, ' ');
                  }
                  return (
                    <span key={p.id} className="id-tell-apart-val">
                      <em>{profileNames[p.id]}</em>: {display}
                    </span>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {sheetOpen && glossarySheet && (
        <GuidanceSheet
          isOpen={sheetOpen}
          onDismiss={() => setSheetOpen(false)}
          title={glossarySheet.label}
          variant="sheet"
        >
          <p>{glossarySheet.plain_language}</p>
          {glossarySheet.botanical_note && (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              <em>{glossarySheet.botanical_note}</em>
            </p>
          )}
          {glossarySheet.svg_key && (
            <img
              src={`/static/trait-diagrams/${glossarySheet.svg_key}.svg`}
              alt={glossarySheet.label}
              style={{ maxWidth: '100%', marginTop: '0.75rem' }}
            />
          )}
        </GuidanceSheet>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IdentificationResults
// ---------------------------------------------------------------------------

function IdentificationResults({
  suggestions,
  refImages = [],
  quotaInfo = null,
  provider = 'plantnet',
  onAccept,
  onDismiss,
  onWishlist,       // unused Phase 1
  onCompare,        // unused Phase 1
  remainingRequests = null,
  dismissLabel = 'None of these',
  emptyDismissLabel,
  onRetry,
  plantId,
  onConfirm,
}) {
  const [expandedRef, setExpandedRef] = useState(null);

  if (!suggestions) return null;

  return (
    <div className="id-results-section">
      <div className="id-results-header">
        <span className="id-results-title">Species Suggestions</span>
        {provider === 'plantid' && quotaInfo ? (
          <span className="id-results-quota">
            {quotaInfo.calls_today} of {quotaInfo.daily_cap} credits used today
          </span>
        ) : (
          remainingRequests != null && (
            <span className="id-results-quota">{remainingRequests} calls remaining today</span>
          )
        )}
      </div>

      {refImages.length > 0 && (
        <div className="id-ref-strip">
          {refImages.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Selected for ID ${i + 1}`}
              className="id-ref-strip-thumb"
              loading="lazy"
            />
          ))}
        </div>
      )}

      {suggestions.length === 0 ? (
        <div className="id-no-results">
          <p className="empty-state">No matches found</p>
          {onDismiss && (
            <button className="btn btn-secondary" onClick={onDismiss}>
              {emptyDismissLabel ?? dismissLabel}
            </button>
          )}
        </div>
      ) : (
        <div className="id-suggestions">
          {suggestions.map((s, i) => (
            <div key={i} className="id-suggestion-wrapper">
              <div className="id-suggestion">
                <div className="id-suggestion-info">
                  <div className="id-suggestion-name">
                    <em>{s.species}</em>
                    {s.common_names?.length > 0 && (
                      <span className="id-suggestion-common"> — {s.common_names[0]}</span>
                    )}
                  </div>
                  <div className="id-suggestion-meta">
                    <span className="id-confidence">{(s.score * 100).toFixed(1)}%</span>
                    {s.family && <span className="id-family">{s.family}</span>}
                    {s.ref_images?.length > 0 && (
                      <button
                        type="button"
                        className="btn-ref-toggle"
                        onClick={() => setExpandedRef(expandedRef === i ? null : i)}
                      >
                        {expandedRef === i ? 'Hide' : 'Compare'}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => onAccept(s)}
                >
                  Select
                </button>
              </div>

              {expandedRef === i && s.ref_images?.length > 0 && (
                <div className="ref-photos">
                  <span className="ref-photos-label">Reference photos:</span>
                  <div className="ref-photos-grid">
                    {s.ref_images.map((url, j) => (
                      <img
                        key={j}
                        src={url}
                        alt={`Reference ${j + 1}`}
                        className="ref-photo-img"
                        loading="lazy"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* "How to tell these apart" — secondary async section */}
          <HowToTellThemApart
            suggestions={suggestions}
            plantId={plantId}
            onConfirm={onConfirm}
            onAccept={onAccept}
          />

          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            {onDismiss && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onDismiss}>
                {dismissLabel}
              </button>
            )}
            {onRetry && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
                Try Again
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default IdentificationResults;
